import React, { FC } from 'react';
import DataTable, { DataTableColumn } from '../../tools/DataTable';
import PaginationBar from '../../../UIComponents/PaginationBar';
import { usePagedList } from '../../../hooks/usePagedList';
import { useAppDispatch } from '../../../hooks/defaultHooks';
import { openOffcanvas } from '../../../store/slices/offcanvasSlice';
import { convertDateToNeedFormat } from '../../../utils/convertDateHandlers';
import { useGetConsumerReferralsQuery } from '../consumer.service';
import { Referral } from '../consumer.types';
import ReferralRewardStatus from '../../referralRewards/ReferralRewardStatus';
import {
  formatDecided,
  formatTokens,
  formatTrigger,
} from '../../referralRewards/formatReferralReward';
import cls from './ConsumerReferralsList.module.scss';

const DEFAULT_PAGE_SIZE = 10;

const columns: DataTableColumn<Referral>[] = [
  {
    key: 'email',
    title: 'Email',
    width: 'minmax(200px, 2fr)',
    render: (referral) => <span title={referral.email}>{referral.email}</span>,
  },
  {
    key: 'signedUpAt',
    title: 'Registered',
    width: 'minmax(150px, 1fr)',
    render: (referral) =>
      convertDateToNeedFormat({ date: referral.signedUpAt, format: 'datetime' }),
  },
  {
    key: 'status',
    title: 'Reward',
    width: 'minmax(130px, 1fr)',
    render: (referral) =>
      referral.reward ? (
        <ReferralRewardStatus reward={referral.reward} />
      ) : (
        <span className={cls.Empty}>Not claimed yet</span>
      ),
  },
  {
    key: 'trigger',
    title: 'Paid for',
    width: 'minmax(120px, 0.8fr)',
    render: (referral) => (referral.reward ? formatTrigger(referral.reward.trigger) : '-'),
  },
  {
    key: 'tokens',
    title: 'Referrer / invitee',
    width: 'minmax(140px, 0.8fr)',
    render: (referral) => (referral.reward ? formatTokens(referral.reward) : '-'),
  },
  {
    key: 'decidedAt',
    title: 'Decided',
    width: 'minmax(150px, 1fr)',
    render: (referral) => (referral.reward ? formatDecided(referral.reward) : '-'),
  },
];

type ConsumerReferralsListProps = {
  consumerId: string;
};

/** The consumers who registered through the referral link, newest first; a row opens the invited one's card */
const ConsumerReferralsList: FC<ConsumerReferralsListProps> = ({ consumerId }) => {
  const dispatch = useAppDispatch();
  const { page, size, setPage, setSize } = usePagedList({ consumerId }, DEFAULT_PAGE_SIZE);
  const { data, isLoading } = useGetConsumerReferralsQuery({ consumerId, page, size });
  const openInvitee = (referral: Referral) =>
    dispatch(openOffcanvas({ type: 'consumerOffcanvas', extra: { id: referral.inviteeId } }));
  return (
    <div data-testid="referrals-list">
      <DataTable
        columns={columns}
        items={data?.referrals}
        isLoading={isLoading}
        emptyText="No one has registered through the referral link"
        rowKey={(referral) => referral.inviteeId}
        onRowClick={openInvitee}
        footer={
          data && (
            <PaginationBar
              page={page}
              size={size}
              countAll={data.countAll}
              onPageChange={setPage}
              onSizeChange={setSize}
              itemsName="referrals"
            />
          )
        }
      />
    </div>
  );
};

export default ConsumerReferralsList;
